import React, { useState } from 'react'
import {
  ArrowRight,
  Phone,
  User,
  Mail,
  MessageSquare,
  Briefcase,
  AlertTriangle,
  Globe,
  Hash,
  CheckCircle,
  XCircle,
  Plus,
  Trash2
} from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'

export interface DiscoveredField {
  path: string
  label: string
  value: any
  type: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'null'
}

export interface TargetVariable {
  key: string
  label: string
  required: boolean
  description: string
}

interface FieldMappingEditorProps {
  discoveredFields: DiscoveredField[]
  targetVariables: TargetVariable[]
  mappings: Record<string, string>
  labels: Record<string, string>
  onChange: (mappings: Record<string, string>, labels: Record<string, string>) => void
}

// Preset context keys that have their own dedicated rows
const PRESET_CONTEXT_KEYS = new Set([
  'context.practice_area',
  'context.urgency',
  'context.source'
])

const TARGET_ICONS: Record<string, React.ReactNode> = {
  'lead.phone': <Phone className="h-4 w-4" />,
  'lead.name': <User className="h-4 w-4" />,
  'lead.email': <Mail className="h-4 w-4" />,
  'lead.message': <MessageSquare className="h-4 w-4" />,
  'context.practice_area': <Briefcase className="h-4 w-4" />,
  'context.urgency': <AlertTriangle className="h-4 w-4" />,
  'context.source': <Globe className="h-4 w-4" />
}

/** Convert a human label like "Date of Incident" to a context key "date_of_incident" */
function labelToKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
}

function formatValue(value: any, maxLength = 40): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'string') {
    const truncated = value.length > maxLength ? value.substring(0, maxLength) + '...' : value
    return `"${truncated}"`
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') return String(value)
  return String(value)
}

function getTypeColor(type: string): string {
  switch (type) {
    case 'string':
      return 'text-green-500'
    case 'number':
      return 'text-blue-500'
    case 'boolean':
      return 'text-purple-500'
    default:
      return 'text-muted-foreground'
  }
}

interface CustomFieldRow {
  id: string
  label: string
  contextKey: string
  sourcePath: string
}

export function FieldMappingEditor({
  discoveredFields,
  targetVariables,
  mappings,
  labels,
  onChange
}: FieldMappingEditorProps) {
  // Initialize custom field rows from existing non-preset context.* mappings
  const [customFields, setCustomFields] = useState<CustomFieldRow[]>(() => {
    const rows: CustomFieldRow[] = []
    for (const key of Object.keys(mappings)) {
      if (!key.startsWith('context.')) continue
      if (PRESET_CONTEXT_KEYS.has(key)) continue
      const contextKey = key.replace('context.', '')
      rows.push({
        id: crypto.randomUUID(),
        label: labels[contextKey] || contextKey.replace(/_/g, ' '),
        contextKey,
        sourcePath: mappings[key] || ''
      })
    }
    return rows
  })

  const handleMappingChange = (targetKey: string, sourcePath: string) => {
    const newMappings = { ...mappings }
    if (sourcePath === '__none__') {
      delete newMappings[targetKey]
    } else {
      newMappings[targetKey] = sourcePath
    }
    onChange(newMappings, labels)
  }

  const syncCustomFields = (fields: CustomFieldRow[]) => {
    setCustomFields(fields)

    // Rebuild mappings: keep all non-custom-context mappings + preset context mappings
    const newMappings: Record<string, string> = {}
    const newLabels: Record<string, string> = {}

    for (const [key, value] of Object.entries(mappings)) {
      if (key.startsWith('context.') && !PRESET_CONTEXT_KEYS.has(key)) continue
      newMappings[key] = value
    }

    // Add custom field mappings
    for (const field of fields) {
      if (field.contextKey && field.sourcePath) {
        newMappings[`context.${field.contextKey}`] = field.sourcePath
      }
      if (field.contextKey && field.label) {
        newLabels[field.contextKey] = field.label
      }
    }

    onChange(newMappings, newLabels)
  }

  const handleAddCustomField = () => {
    syncCustomFields([
      ...customFields,
      { id: crypto.randomUUID(), label: '', contextKey: '', sourcePath: '' }
    ])
  }

  const handleCustomFieldLabelChange = (id: string, newLabel: string) => {
    const updated = customFields.map(f =>
      f.id === id ? { ...f, label: newLabel, contextKey: labelToKey(newLabel) } : f
    )
    syncCustomFields(updated)
  }

  const handleCustomFieldSourceChange = (id: string, sourcePath: string) => {
    const updated = customFields.map(f =>
      f.id === id ? { ...f, sourcePath: sourcePath === '__none__' ? '' : sourcePath } : f
    )
    syncCustomFields(updated)
  }

  const handleRemoveCustomField = (id: string) => {
    syncCustomFields(customFields.filter(f => f.id !== id))
  }

  // Separate required and optional target variables
  const requiredVars = targetVariables.filter(v => v.required)
  const optionalVars = targetVariables.filter(v => !v.required)

  // Group optional vars by category
  const leadVars = optionalVars.filter(v => v.key.startsWith('lead.'))
  const contextVars = optionalVars.filter(v => v.key.startsWith('context.'))

  // Count total mapped fields (presets + custom)
  const totalMapped = Object.values(mappings).filter(Boolean).length

  const renderFieldSelector = (target: TargetVariable) => {
    const currentMapping = mappings[target.key]
    const currentField = discoveredFields.find(f => f.path === currentMapping)

    return (
      <div
        key={target.key}
        className={`rounded-lg border p-4 ${
          target.required && !currentMapping
            ? 'border-red-500/50 bg-red-500/5'
            : currentMapping
              ? 'border-green-500/30 bg-green-500/5'
              : 'border-muted'
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          {/* Target Variable Info */}
          <div className="flex-shrink-0 min-w-[180px]">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                {TARGET_ICONS[target.key] || <Hash className="h-4 w-4" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{target.label}</span>
                  {target.required && (
                    <Badge variant="destructive" className="text-xs px-1 py-0">
                      Required
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{target.description}</p>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex items-center pt-2">
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </div>

          {/* Source Field Selector */}
          <div className="flex-1 min-w-[200px]">
            <Select
              value={currentMapping || '__none__'}
              onValueChange={(value) => handleMappingChange(target.key, value)}
            >
              <SelectTrigger className={`w-full ${
                target.required && !currentMapping ? 'border-red-500' : ''
              }`}>
                <SelectValue placeholder="Select a field..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  <span className="text-muted-foreground">Not mapped</span>
                </SelectItem>
                {discoveredFields.map((field) => (
                  <SelectItem key={field.path} value={field.path}>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-muted px-1 rounded">
                        {field.path}
                      </code>
                      <span className={`text-xs ${getTypeColor(field.type)}`}>
                        {formatValue(field.value, 20)}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Show sample value if mapped */}
            {currentField && (
              <p className="mt-1 text-xs text-muted-foreground truncate">
                Sample: <span className={getTypeColor(currentField.type)}>
                  {formatValue(currentField.value, 50)}
                </span>
              </p>
            )}
          </div>

          {/* Status Icon */}
          <div className="pt-2">
            {currentMapping ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : target.required ? (
              <XCircle className="h-5 w-5 text-red-500" />
            ) : (
              <div className="h-5 w-5" />
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">Match Your Form Fields</h4>
          <p className="text-sm text-muted-foreground">
            Tell us which fields contain lead contact information
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">
            {discoveredFields.length} fields found
          </span>
          <span className="text-muted-foreground">|</span>
          <span className={
            totalMapped === 0
              ? 'text-red-500'
              : 'text-green-500'
          }>
            {totalMapped} matched
          </span>
        </div>
      </div>

      {/* Required Fields Section */}
      <div className="space-y-3">
        <Label className="text-sm font-medium flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-red-500" />
          Required Fields
        </Label>
        <div className="space-y-3">
          {requiredVars.map(renderFieldSelector)}
        </div>
      </div>

      {/* Lead Info Fields */}
      {leadVars.length > 0 && (
        <div className="space-y-3">
          <Label className="text-sm font-medium text-muted-foreground">
            Lead Information (Optional)
          </Label>
          <div className="space-y-3">
            {leadVars.map(renderFieldSelector)}
          </div>
        </div>
      )}

      {/* Context Fields */}
      {contextVars.length > 0 && (
        <div className="space-y-3">
          <Label className="text-sm font-medium text-muted-foreground">
            AI Context (Optional)
          </Label>
          <p className="text-xs text-muted-foreground -mt-1">
            These values are passed to the AI assistant for personalized conversation
          </p>
          <div className="space-y-3">
            {contextVars.map(renderFieldSelector)}
          </div>
        </div>
      )}

      {/* Additional Context Fields (dynamic) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">
              Additional Context Fields
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Map any extra form fields so the AI has full context about the lead
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddCustomField}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Field
          </Button>
        </div>

        {customFields.length > 0 && (
          <div className="space-y-3">
            {customFields.map((field) => {
              const currentField = discoveredFields.find(f => f.path === field.sourcePath)
              return (
                <div
                  key={field.id}
                  className={`rounded-lg border p-4 ${
                    field.sourcePath && field.label
                      ? 'border-green-500/30 bg-green-500/5'
                      : 'border-muted'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Label Input */}
                    <div className="flex-shrink-0 min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                          <Hash className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <Input
                            placeholder="Field label (e.g., Date of Incident)"
                            value={field.label}
                            onChange={(e) => handleCustomFieldLabelChange(field.id, e.target.value)}
                            className="h-8 text-sm"
                          />
                          {field.contextKey && (
                            <p className="text-[10px] text-muted-foreground mt-0.5 ml-1">
                              Key: {field.contextKey}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex items-center pt-2">
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>

                    {/* Source Field Selector */}
                    <div className="flex-1 min-w-[200px]">
                      <Select
                        value={field.sourcePath || '__none__'}
                        onValueChange={(value) => handleCustomFieldSourceChange(field.id, value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a field..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">
                            <span className="text-muted-foreground">Not mapped</span>
                          </SelectItem>
                          {discoveredFields.map((df) => (
                            <SelectItem key={df.path} value={df.path}>
                              <div className="flex items-center gap-2">
                                <code className="text-xs bg-muted px-1 rounded">
                                  {df.path}
                                </code>
                                <span className={`text-xs ${getTypeColor(df.type)}`}>
                                  {formatValue(df.value, 20)}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {currentField && (
                        <p className="mt-1 text-xs text-muted-foreground truncate">
                          Sample: <span className={getTypeColor(currentField.type)}>
                            {formatValue(currentField.value, 50)}
                          </span>
                        </p>
                      )}
                    </div>

                    {/* Delete Button */}
                    <div className="pt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveCustomField(field.id)}
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* All Discovered Fields Preview */}
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium">Your Form Fields</Label>
            <Badge variant="outline">{discoveredFields.length} fields</Badge>
          </div>
          <ScrollArea className="h-40">
            <div className="space-y-1 font-mono text-xs">
              {discoveredFields.map((field) => (
                <div
                  key={field.path}
                  className={`flex items-center gap-2 p-1.5 rounded hover:bg-muted ${
                    Object.values(mappings).includes(field.path)
                      ? 'bg-green-500/10'
                      : ''
                  }`}
                >
                  <span className="text-muted-foreground min-w-[140px] truncate">
                    {field.path}
                  </span>
                  <span className="text-muted-foreground">:</span>
                  <span className={getTypeColor(field.type)}>
                    {formatValue(field.value, 60)}
                  </span>
                  {Object.values(mappings).includes(field.path) && (
                    <CheckCircle className="h-3 w-3 text-green-500 ml-auto flex-shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  )
}
